import React, { useState } from 'react';
import { useGameState } from '../hooks/useGameState';
import { Dashboard } from '../components/Dashboard';
import { DecisionsPanel } from '../components/DecisionsPanel';
import { OfficeVisualization } from '../components/OfficeVisualization';
import { LoginScreen } from '../components/LoginScreen';
import { OnboardingModal } from '../components/OnboardingModal';
import { TurnAdvance } from '../components/TurnAdvance';
import { WinState } from '../components/WinState';
import { BankruptcyState } from '../components/BankruptcyState';
import { QuarterlyReport } from '../components/QuarterlyReport';
import { Decisions } from '../types/game';
export function GamePage() {
  const {
    state,
    advanceTurn,
    resetGame,
    lastDecisions
  } = useGameState();
  // UI Flow State
  const [hasLoggedIn, setHasLoggedIn] = useState(false);
  const [hasOnboarded, setHasOnboarded] = useState(false);
  const [isAdvancingTurn, setIsAdvancingTurn] = useState(false);
  const [showQuarterlyReport, setShowQuarterlyReport] = useState(false);
  const [pendingDecisions, setPendingDecisions] = useState<Decisions | null>(null);
  // Handlers
  const handleLogin = () => setHasLoggedIn(true);
  const handleOnboardingComplete = () => setHasOnboarded(true);
  const handleCommitDecisions = (decisions: Decisions) => {
    setPendingDecisions(decisions);
    setIsAdvancingTurn(true);
  };
  const handleTurnComplete = () => {
    if (pendingDecisions) {
      advanceTurn(pendingDecisions);
      setPendingDecisions(null);
      setIsAdvancingTurn(false);
      // Show report after turn processing
      setShowQuarterlyReport(true);
    }
  };
  const handleReportContinue = () => {
    setShowQuarterlyReport(false);
  };
  const handleReset = () => {
    resetGame();
    setShowQuarterlyReport(false);
    // Optional: Reset UI flow if desired, or just restart game loop
    // setHasOnboarded(false);
  };
  // 1. Login Screen
  if (!hasLoggedIn) {
    return <LoginScreen onLogin={handleLogin} data-id="element-398" />;
  }
  // 2. Onboarding
  if (!hasOnboarded) {
    return <OnboardingModal onComplete={handleOnboardingComplete} data-id="element-399" />;
  }
  // 3. Main Game Loop
  return <div className="min-h-screen bg-background text-text-primary p-4 md:p-6 lg:p-8 flex flex-col" data-id="element-400">
      {/* Overlays */}
      {isAdvancingTurn && <TurnAdvance turn={state.turn + 1} onComplete={handleTurnComplete} data-id="element-401" />}

      {showQuarterlyReport && !state.isGameOver && <QuarterlyReport gameState={state} decisions={lastDecisions} onContinue={handleReportContinue} data-id="element-402" />}

      {state.gameStatus === 'victory' && <WinState gameState={state} onReset={handleReset} data-id="element-403" />}

      {state.gameStatus === 'bankrupt' && <BankruptcyState gameState={state} onReset={handleReset} data-id="element-404" />}

      {/* Main Layout */}
      <div className="max-w-7xl mx-auto w-full flex-1 flex flex-col space-y-6" data-id="element-405">
        {/* Top: Dashboard HUD */}
        <section data-id="element-406">
          <Dashboard gameState={state} data-id="element-407" />
        </section>

        {/* Bottom: Split View */}
        <section className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-[500px]" data-id="element-408">
          {/* Left: Office Viz (4 cols) */}
          <div className="lg:col-span-4 h-full" data-id="element-409">
            <OfficeVisualization engineers={state.engineers} sales={state.sales} quality={state.productQuality} data-id="element-410" />
          </div>

          {/* Right: Decisions Panel (8 cols) */}
          <div className="lg:col-span-8 h-full" data-id="element-411">
            <DecisionsPanel gameState={state} onCommit={handleCommitDecisions} data-id="element-412" />
          </div>
        </section>
      </div>
    </div>;
}